import { apiClient } from "@lobby/api-client";
import {
  onlineManager,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";

import { APIError, CLIENT_ERRORS, ClientError, emitter, useAuth } from "../../shared";

import type { SendOptions } from "@lobby/api-client";

type LoginParams = {
  successCallback?: () => void;
};

export const Player = {
  useLogIn(params?: LoginParams) {
    const queryClient = useQueryClient();
    const route = useRouter();

    return useMutation({
      mutationKey: ["Player.logIn"],
      mutationFn: (params: SendOptions<"Player.logIn">["params"]) =>
        apiClient.send<"Player.logIn">({
          endpoint: "Player.logIn",
          params,
        }),
      onSuccess: async ({ result }) => {
        if (!result) return;

        queryClient.removeQueries({ type: "inactive" });

        if (result.isTwoFactorAuthRequired) {
          await queryClient.refetchQueries({ queryKey: ["Player.getCurrent"] });
        } else {
          window.dispatchEvent(new Event("player:login"));
          await queryClient.refetchQueries({ type: "active" });
          emitter.emit("PLAYER_LOGIN");
        }

        route.invalidate();
        params?.successCallback?.();
      },
    });
  },

  useLogOut() {
    const queryClient = useQueryClient();
    const route = useRouter();

    return useMutation({
      mutationKey: ["Player.logOut"],
      mutationFn: () =>
        apiClient.send<"Player.logOut">({
          endpoint: "Player.logOut",
        }),
      onSuccess: () => {
        queryClient.setQueryData(["Player.getCurrent"], {});
        route.invalidate();
        window.dispatchEvent(new Event("player:logout"));
      },
    });
  },

  usePlayer() {
    return useQuery({
      refetchInterval: 1000 * 15,
      queryKey: ["Player.getCurrent"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Player.getCurrent",
          params: { isActive: "true" },
        }),
      select: (data) => data.result,
    });
  },

  usePlayerSuspense() {
    return useSuspenseQuery({
      refetchInterval: 1000 * 15,
      queryKey: ["Player.getCurrent"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Player.getCurrent",
          params: { isActive: "true" },
        }),
      select: (data) => data.result,
    });
  },

  usePanic() {
    return useQuery({
      refetchInterval: 1000 * 15,
      queryKey: ["Player.getCurrent"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Player.getCurrent",
          params: { isActive: "true" },
        }),
    });
  },

  useUpdatePlayer() {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (params: SendOptions<"Player.update">["params"]) =>
        apiClient.send<"Player.update">({
          endpoint: "Player.update",
          params,
        }),
      onSuccess: (data) => {
        if (data.error) {
          console.error(data.error);
        }

        if (data.result === "success") {
          queryClient.invalidateQueries({ queryKey: ["Player.getCurrent"] });
        }
      },
    });
  },

  useBonusHistory() {
    const { isAuth } = useAuth();

    return useQuery({
      staleTime: 1000 * 15,
      enabled: isAuth,
      queryKey: ["Bonus.getHistory"],
      queryFn: async () => {
        const result = await apiClient.send({
          endpoint: "Bonus.getHistory",
        });

        if (result.error) {
          console.error(result.error);
        }

        return result;
      },
      select: (data) => data?.result?.data,
    });
  },

  useBonusDescription() {
    const { isAuth } = useAuth();

    return useQuery({
      staleTime: 1000 * 15,
      enabled: isAuth,
      queryKey: ["Bonus.getDescription"],
      queryFn: async () => {
        const result = await apiClient.send({
          endpoint: "Bonus.getDescription",
        });

        if (result.error) {
          console.error(result.error);
        }

        return result;
      },
    });
  },

  useActivateBonus() {
    const queryClient = useQueryClient();

    return useMutation({
      networkMode: "always",
      mutationKey: ["Bonus.activate"],
      mutationFn: () => {
        if (!onlineManager.isOnline()) {
          emitter.emit("ERROR", { error: new ClientError(CLIENT_ERRORS[0]) });
        }

        return apiClient.send<"Bonus.activate">({
          endpoint: "Bonus.activate",
        });
      },
      onError: (error) => emitter.emit("ERROR", { error: new APIError(error.message) }),
      onSuccess: (data) => {
        if (data.error) {
          const error = new APIError(data.error.message, {
            code: data.error.code,
          });

          emitter.emit("ERROR", { error });
        }

        queryClient.invalidateQueries({ queryKey: ["Player.getCurrent"] });
        queryClient.invalidateQueries({ queryKey: ["Player.getBalancesEx"] });
        queryClient.invalidateQueries({ queryKey: ["Bonus.getHistory"] });
      },
    });
  },

  useBalances() {
    const { isAuth } = useAuth();

    return useQuery({
      refetchInterval: 1000 * 15,
      staleTime: 1000 * 15,
      enabled: isAuth,
      queryKey: ["Player.getBalancesEx"],
      queryFn: async () => {
        const result = await apiClient.send({
          endpoint: "Player.getBalancesEx",
        });

        if (result.error) {
          console.error(result.error);
        }

        return result;
      },
      select: (data) => data.result,
    });
  },

  useBalancesSuspense() {
    return useSuspenseQuery({
      refetchInterval: 1000 * 15,
      staleTime: 1000 * 15,
      queryKey: ["Player.getBalancesEx"],
      queryFn: () =>
        apiClient.send({
          endpoint: "Player.getBalancesEx",
        }),
    });
  },

  useQueryBalances() {
    const { isAuth } = useAuth();

    return useQuery({
      refetchInterval: 1000 * 15,
      staleTime: 1000 * 15,
      enabled: isAuth,
      queryKey: ["Player.getBalancesEx"],
      queryFn: async () => {
        return await apiClient.send({
          endpoint: "Player.getBalancesEx",
        });
      },
    });
  },

  useChangePassword() {
    return useMutation({
      mutationKey: ["Player.changePassword"],
      mutationFn: (params: SendOptions<"Player.changePassword">["params"]) =>
        apiClient.send<"Player.changePassword">({
          endpoint: "Player.changePassword",
          params,
        }),
      onSuccess: (data) => {
        if (data.error) {
          console.error(data.error);
        }
      },
    });
  },

  useRegister() {
    return useMutation({
      mutationKey: ["Player.register"],
      mutationFn: (params: SendOptions<"Player.register">["params"]) =>
        apiClient.send<"Player.register">({
          endpoint: "Player.register",
          params,
        }),
      onSuccess: (data) => {
        if (data.error) {
          console.error(data.error);
        }
      },
    });
  },

  useResetPassword() {
    return useMutation({
      mutationKey: ["Player.resetPassword"],
      mutationFn: (params: SendOptions<"Player.resetPassword">["params"]) =>
        apiClient.send<"Player.resetPassword">({
          endpoint: "Player.resetPassword",
          params,
        }),
      onSuccess: (data) => {
        if (data.error) {
          console.error(data.error);
        }
      },
    });
  },
};
